import { FormTherapyRow } from "./therapyRxLabel";

/**
 * The row shape every Rx form editor (Kasse, BG, Blanko, Bundeswehr, Zahnarzt,
 * Privat) uses for its Heilmittel table.
 *
 * It has always existed implicitly — built by hand in six places and passed
 * around as `any`. Naming it is what makes the MD-232 mistakes visible: the Rx
 * page crashed on `therapy.therapy.id` because nothing recorded that a row's
 * `therapy` is a resolved {@link FormTherapyRow}, not a nested API line.
 *
 * This is deliberately a FORM type, not a DTO. It is what the editor binds to;
 * it is neither what the API returns nor what gets sent back. Keeping the three
 * apart is the point — see the Tier 2 DTO split.
 */
export interface RxFormRow {
  /** Row id: the TherapyRx id when persisted, otherwise a client-side uuid. */
  id: string;

  /**
   * MD-232: the prescribed TERM as text — what the server resolves through the
   * alias table on save, and the only field naming the term on the way out
   * (`toTherapyRxWriteDto` sends `rawText` / `prescribedTherapyId`, never
   * `therapy`).
   *
   * It must therefore SURVIVE A ROUND TRIP: loading an existing Rx into the
   * editor and saving it unchanged has to send back the term it came with.
   * Without this field the row shape silently dropped it, so editing any
   * existing Rx — even without touching the Heilmittel — saved a line naming
   * no term and the server answered "Heilmittel unbekannt".
   */
  rawText?: string | null;

  /** Denormalised from the Rx for display in the table. */
  date?: string | Date | null;

  /**
   * Always an object by the time it reaches the table — `formTherapyFromLine`
   * guarantees it. `abbreviation` is the value the cell edits and what
   * `validateTherapy` checks, so it must stay an abbreviation, not a label.
   */
  therapy: FormTherapyRow;

  /** Blank while the user is still typing, hence the string. */
  amount: number | string | null;

  /** `{ id, text }` once chosen; the raw string while mid-edit. */
  frequency:
    | { id: string; text: string; prefferedValue?: string | number }
    | string
    | null;

  /**
   * MD-232: legacy. A Doppelbehandlung is `unitsPerDay > 1` on ONE line now,
   * not a second shadow line. Retained only as the checkbox's view state; the
   * forms keep it in sync with `unitsPerDay`, and every save channel reads
   * `unitsPerDay` (falling back to this flag for old row shapes).
   *
   * @deprecated use the line's `unitsPerDay`
   */
  isDoubled?: boolean;

  /**
   * Units per treatment day (MD-232): 2 = Doppelbehandlung. THE doubling
   * datum — carried through the round trip and read by both save channels.
   */
  unitsPerDay?: number | null;

  rxSeries?: { id?: string; label?: string } | null;
}

/**
 * The term a form row names, as text — "" when it names none.
 *
 * MD-232 moved the row's identity from the billable line item to the term, but
 * the six form builders kept gating saves on `row.therapy.id`. That id is only
 * populated by `formTherapyFromLine` when a row is LOADED from the API; the
 * Heilmittel field sets `rawText` and `therapy.abbreviation` and never an id.
 * So every freshly entered row was silently dropped from the payload while
 * loaded rows survived — the asymmetry that reads as "Heilmittel unbekannt".
 *
 * Order mirrors `toTherapyRxWriteDto`: whatever is sent as the term is what
 * decides whether the row is worth sending.
 */
export const rowTermText = (row: any): string => {
  if (!row) return "";
  if (row.rawText) return row.rawText;
  if (row.prescribedTherapyId) return row.prescribedTherapyId;
  const therapy = row.therapy;
  if (typeof therapy === "string") return therapy;
  return therapy?.abbreviation || therapy?.id || "";
};

/** Whether a row names a term at all — the save-time "is this row real" test. */
export const rowNamesTerm = (row: any): boolean => rowTermText(row) !== "";
